import classNames from "classnames";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { ArrowDown } from "components/icons/ArrowDown";
import {
  components,
  ControlProps,
  DropdownIndicatorProps,
  MultiValue,
  SingleValue,
} from "react-select";
import { useGetSupportingDocumentTagsQuery } from "store/apis/documents";

type DocumentTagComboboxProps = {
  groupId: string;
  selectedUuids: string[];
  onChange: (uuids: string[]) => void;
};

const DocumentTagComboboxControl = ({
  children,
  ...props
}: ControlProps<OptionData>) => {
  const hasValue = ((props.selectProps.value as OptionData[]) || []).length > 0;

  return (
    <components.Control
      {...props}
      className={classNames(
        "t-px-2 !t-min-h-8 t-rounded t-border t-border-solid !t-cursor-pointer t-flex t-items-center hover:t-border-neutral-10 hover:t-bg-surface-lighter-grey",
        {
          "t-border-neutral-0 t-bg-surface-lighter-grey": !hasValue,
          "t-border-surface-transparent": hasValue && !props.isFocused,
          "!t-border-neutral-10 t-bg-surface-lighter-grey": props.isFocused,
        }
      )}
    >
      {children}
    </components.Control>
  );
};

const DocumentTagComboboxDropdownIndicator = (
  props: DropdownIndicatorProps<OptionData>
) => (
  <div
    className={classNames(
      "t-flex t-items-center t-w-0 t-opacity-0 group-hover:t-opacity-100 group-hover:t-w-auto",
      {
        "!t-opacity-100 !t-w-auto t-rotate-180": props.selectProps.menuIsOpen,
      }
    )}
  >
    <ArrowDown color="currentColor" />
  </div>
);

export const DocumentTagCombobox = ({
  groupId,
  selectedUuids,
  onChange,
}: DocumentTagComboboxProps) => {
  const { data: tagOptions = [] } = useGetSupportingDocumentTagsQuery(
    { groupId },
    { skip: !groupId }
  );

  const options: OptionData[] = tagOptions.map((tag) => ({
    value: tag.uuid,
    label: tag.label || tag.title,
  }));

  const value = options.filter((option) =>
    selectedUuids.includes(option.value)
  );

  const onSelectChange = (
    selected: MultiValue<OptionData> | SingleValue<OptionData>
  ) => {
    const selectedOptions = Array.isArray(selected) ? selected : [];
    onChange(selectedOptions.map((option) => option.value));
  };

  return (
    <div className="t-group" onClick={(e) => e.stopPropagation()}>
      <Combobox
        isMulti
        isClearable={false}
        size="small"
        placeholder="Select tags"
        options={options}
        value={value}
        multiValueShowLimit={1}
        menuPortalTarget={document.body}
        menuPlacement="auto"
        menuShouldScrollIntoView={false}
        components={{
          Control: DocumentTagComboboxControl,
          DropdownIndicator: DocumentTagComboboxDropdownIndicator,
        }}
        onChange={onSelectChange}
      />
    </div>
  );
};
